import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';

export default function BulkOrdersPanel({ onNavigate }) {
  const [animals, setAnimals]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('');
  const [selections, setSelections] = useState({}); // { animalId: qty }
  const [adding, setAdding]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const fetchAnimals = () => {
    setLoading(true);
    const params = {};
    if (search)   params.search   = search;
    if (category) params.category = category;
    api.get('/api/butcher/browse', { params })
      .then(r => setAnimals(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAnimals(); }, []); // eslint-disable-line

  const toggleSelect = (animal) => {
    setSelections(prev => {
      if (prev[animal.id]) {
        const next = { ...prev };
        delete next[animal.id];
        return next;
      }
      return { ...prev, [animal.id]: 1 };
    });
  };

  const setQty = (id, val, max) => {
    const qty = Math.min(Math.max(1, Number(val)), max);
    setSelections(prev => ({ ...prev, [id]: qty }));
  };

  const selectedList = animals.filter(a => selections[a.id]);
  const totalAmount  = selectedList.reduce(
    (sum, a) => sum + Number(a.price_inr) * (selections[a.id] || 1), 0
  );

  const getListingStats = (a) => {
    const count      = Number(a.quantity || 0);
    const totalWt    = count * Number(a.weight_kg || 0);
    const avgWt      = Number(a.weight_kg || 0);
    const pricePerKg = a.weight_kg ? Math.round(Number(a.price_inr) / Number(a.weight_kg)) : null;
    const lotPrice   = Number(a.price_inr) * count;
    return { count, totalWt, avgWt, pricePerKg, lotPrice };
  };

  const totalAnimalsSelected = selectedList.reduce((s, a) => s + (selections[a.id] || 1), 0);
  const totalWeightSelected  = selectedList.reduce((s, a) => s + Number(a.weight_kg || 0) * (selections[a.id] || 1), 0);

  const handleAddToCart = async () => {
    if (selectedList.length === 0) { setError('Select at least one animal'); return; }
    setAdding(true); setError(''); setSuccess('');
    try {
      // Build cart items and sync
      const cartItems = selectedList.map(a => ({
        id:        a.id,
        name:      a.name,
        price_inr: a.price_inr,
        image_url: a.image_url,
        quantity:  selections[a.id] || 1,
        seller_id: a.seller_id,
      }));

      // Get existing cart and merge
      const existing = JSON.parse(localStorage.getItem('cart') || '[]');
      const merged = [...existing];
      for (const item of cartItems) {
        const idx = merged.findIndex(c => c.id === item.id);
        if (idx >= 0) merged[idx].quantity += item.quantity;
        else merged.push(item);
      }

      await api.post('/api/butcher/cart/sync', { cart: merged });
      setSuccess(`${selectedList.length} animal(s) added to cart!`);
      setSelections({});
      setTimeout(() => { setSuccess(''); onNavigate('cart'); }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Summary bar ── */}
      {selectedList.length > 0 && (
        <div className="bg-primary rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-5">
            <div>
              <p className="text-white/70 text-[10px] uppercase tracking-wide">Listings</p>
              <p className="text-white font-bold text-lg leading-tight">{selectedList.length}</p>
            </div>
            <div>
              <p className="text-white/70 text-[10px] uppercase tracking-wide">Animals</p>
              <p className="text-white font-bold text-lg leading-tight">{totalAnimalsSelected}</p>
            </div>
            {totalWeightSelected > 0 && (
              <div>
                <p className="text-white/70 text-[10px] uppercase tracking-wide">Total weight</p>
                <p className="text-white font-bold text-lg leading-tight">{totalWeightSelected.toFixed(1)} kg</p>
              </div>
            )}
            <div>
              <p className="text-white/70 text-[10px] uppercase tracking-wide">Order total</p>
              <p className="text-white font-bold text-lg leading-tight">₹{totalAmount.toLocaleString('en-IN')}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setSelections({})}
              className="px-4 py-2 rounded-full bg-white/10 text-white text-sm font-bold hover:bg-white/20 transition-all">
              Clear
            </button>
            <button onClick={handleAddToCart} disabled={adding}
              className="px-4 py-2 rounded-full bg-white/20 text-white text-sm font-bold hover:bg-white/30 transition-all disabled:opacity-50 flex items-center gap-2">
              <span className="material-symbols-outlined text-[16px]">shopping_cart</span>
              {adding ? 'Adding...' : 'Add to Cart'}
            </button>
            <button onClick={async () => {
              if (selectedList.length === 0) { setError('Select at least one animal'); return; }
              setAdding(true); setError('');
              try {
                const cartItems = selectedList.map(a => ({
                  id:       a.id,
                  name:     a.name,
                  price:    Number(a.price_inr),
                  image:    a.image_url || null,
                  category: a.category,
                  seller_id: a.seller_id,
                  quantity: selections[a.id] || 1,
                }));
                await api.post('/api/customer/cart/sync', { items: cartItems });
                setSelections({});
                onNavigate('cart');
              } catch (err) {
                setError(err.response?.data?.error || 'Failed');
              } finally { setAdding(false); }
            }} disabled={adding}
              className="px-4 py-2 rounded-full bg-white text-primary text-sm font-bold hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2">
              <span className="material-symbols-outlined text-[16px]">bolt</span>
              Buy Now
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-base">check_circle</span>{success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-base">error</span>{error}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10">
        <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
          <h3 className="font-bold text-lg">Bulk Order Animals</h3>
          <div className="flex gap-2 flex-wrap">
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchAnimals()}
              placeholder="Search animals..."
              className="px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-full text-sm outline-none focus:ring-1 focus:ring-primary" />
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-full text-sm outline-none">
              <option value="">All Categories</option>
              {['Goat', 'Sheep', 'Cow', 'Camel'].map(c => <option key={c}>{c}</option>)}
            </select>
            <button onClick={fetchAnimals}
              className="px-4 py-2 rounded-full bg-primary text-white text-sm font-bold hover:opacity-90 transition-all">
              Search
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-secondary text-sm">Loading...</div>
        ) : animals.length === 0 ? (
          <div className="py-10 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant">inventory_2</span>
            <p className="mt-2 text-sm font-medium text-secondary">No animals found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {animals.map(a => {
              const isSelected = !!selections[a.id];
              const qty = selections[a.id] || 1;
              const maxQty = a.quantity || 1;
              const { count, totalWt, avgWt, pricePerKg, lotPrice } = getListingStats(a);
              return (
                <div key={a.id}
                  className={`border-2 rounded-2xl overflow-hidden transition-all ${
                    isSelected
                      ? 'border-primary shadow-md shadow-primary/10'
                      : 'border-outline-variant/20 hover:border-primary/30'
                  }`}>
                  {/* Image — clicking toggles select */}
                  <div className="relative cursor-pointer" onClick={() => toggleSelect(a)}>
                    {a.image_url
                      ? <img src={a.image_url} alt={a.name} className="w-full h-36 object-cover" />
                      : <div className="w-full h-36 bg-surface-container flex items-center justify-center">
                          <span className="material-symbols-outlined text-4xl text-outline-variant">pets</span>
                        </div>
                    }
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-sm">check</span>
                      </div>
                    )}
                  </div>

                  {/* Listing stats band */}
                  <div className="grid grid-cols-4 divide-x divide-outline-variant/20
                                  bg-surface-container/50 border-y border-outline-variant/10">
                    {[
                      { label: 'In listing', value: `${count}`, unit: count === 1 ? 'animal' : 'animals' },
                      { label: 'Total wt',   value: totalWt > 0 ? `${totalWt} kg` : '—',  unit: null },
                      { label: 'Avg wt',     value: avgWt   > 0 ? `${avgWt} kg`   : '—',  unit: null },
                      { label: 'Per kg',     value: pricePerKg  ? `₹${pricePerKg.toLocaleString('en-IN')}` : '—', unit: null },
                    ].map(({ label, value, unit }) => (
                      <div key={label} className="py-2 px-1.5 text-center">
                        <p className="text-[9px] uppercase tracking-wide text-secondary leading-none mb-0.5">{label}</p>
                        <p className="text-[12px] font-bold text-on-surface leading-tight">{value}</p>
                        {unit && <p className="text-[9px] text-secondary leading-none">{unit}</p>}
                      </div>
                    ))}
                  </div>

                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-sm">{a.name}</p>
                        <p className="text-[11px] text-secondary">{a.category} · {a.weight_kg ? `${a.weight_kg}kg` : '—'}</p>
                        <p className="text-[11px] text-secondary">
                          Available: {a.quantity || 0} units
                        </p>
                      </div>
                      <p className="font-black text-primary text-sm">₹{Number(a.price_inr).toLocaleString('en-IN')}</p>
                    </div>

                    {/* Quantity selector — only when selected */}
                    {isSelected && (
                      <div className="flex items-center gap-2 pt-1" onClick={e => e.stopPropagation()}>
                        <label className="text-[10px] font-bold uppercase text-secondary">Qty:</label>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setQty(a.id, qty - 1, maxQty)}
                            className="w-7 h-7 rounded-full border border-outline-variant/40 flex items-center justify-center hover:bg-surface-container text-sm font-bold">
                            −
                          </button>
                          <input
                            type="number" min="1" max={maxQty}
                            value={qty}
                            onKeyDown={e => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
                            onChange={e => setQty(a.id, e.target.value, maxQty)}
                            className="w-14 text-center text-sm font-bold border border-outline-variant/40 rounded-lg px-2 py-1 outline-none focus:ring-1 focus:ring-primary"
                          />
                          <button onClick={() => setQty(a.id, qty + 1, maxQty)}
                            className="w-7 h-7 rounded-full border border-outline-variant/40 flex items-center justify-center hover:bg-surface-container text-sm font-bold">
                            +
                          </button>
                        </div>
                        <span className="text-[10px] text-secondary">/ {maxQty}</span>
                      </div>
                    )}

                    {/* Subtotal when selected */}
                    {isSelected && (
                      <div className="flex justify-between items-center px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <span className="text-[11px] text-emerald-700">
                          ₹{Number(a.price_inr).toLocaleString('en-IN')} × {qty}
                        </span>
                        <span className="text-[12px] font-black text-emerald-800">
                          = ₹{(Number(a.price_inr) * qty).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}

                    <button onClick={() => toggleSelect(a)}
                      className={`w-full py-2 rounded-xl text-xs font-bold transition-all ${
                        isSelected
                          ? 'bg-red-50 text-red-600 hover:bg-red-100'
                          : 'bg-primary/10 text-primary hover:bg-primary/20'
                      }`}>
                      {isSelected ? 'Remove' : 'Select'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}